import { sql } from "drizzle-orm";
import { check, integer, jsonb, numeric, pgTable, text, timestamp, uuid, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";

import { baFinancialPeriods, confidenceStatusEnum } from "./schema-business-analytics.js";
import { deals } from "./schema-deals.js";

export const dealObligations = pgTable("crm_x_deal_obligations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  dealId: uuid("deal_id")
    .notNull()
    .references(() => deals.id, { onDelete: "cascade" }),
  facilityLabel: text("facility_label").notNull(),
  limitAmount: numeric("limit_amount", { precision: 15, scale: 2 }).notNull(),
  interestRate: numeric("interest_rate", { precision: 15, scale: 5 }).notNull(),
  termMonths: integer("term_months").notNull(),
  ioMonths: integer("io_months").notNull().default(0),
  repaymentType: text("repayment_type").notNull().default("principal_and_interest"),
  rateStressable: boolean("rate_stressable").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const financialReviewAudit = pgTable(
  "crm_x_financial_review_audit",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id"),
    financialPeriodId: uuid("financial_period_id")
      .notNull()
      .references(() => baFinancialPeriods.id, { onDelete: "cascade" }),
    fromStatus: confidenceStatusEnum("from_status"),
    toStatus: confidenceStatusEnum("to_status").notNull(),
    action: text("action").notNull(),
    actorId: uuid("actor_id"),
    actorRole: text("actor_role"),
    overrideReason: text("override_reason"),
    crossfootDeltas: jsonb("crossfoot_deltas"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    overrideReasonRequired: check(
      "chk_financial_review_audit_override_reason",
      sql`${table.action} <> 'override' OR length(trim(coalesce(${table.overrideReason}, ''))) > 0`,
    ),
  }),
);

export const insertDealObligationSchema = createInsertSchema(dealObligations).omit({ id: true, createdAt: true, updatedAt: true });
export const selectDealObligationSchema = createSelectSchema(dealObligations);
export const insertFinancialReviewAuditSchema = createInsertSchema(financialReviewAudit).omit({ id: true, createdAt: true });
export const selectFinancialReviewAuditSchema = createSelectSchema(financialReviewAudit);

export type DealObligation = typeof dealObligations.$inferSelect;
export type InsertDealObligation = typeof dealObligations.$inferInsert;
export type FinancialReviewAudit = typeof financialReviewAudit.$inferSelect;
export type InsertFinancialReviewAudit = typeof financialReviewAudit.$inferInsert;
